import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(undefined);

// Available themes in the application
// 'console-dark' / 'console-light' are the "Institutional Console" redesign themes
// (DESIGN-SPEC.md §2) — additive, selectable, and (as of issue #238) the DEFAULT
// theme for new/logged-out sessions. Legacy themes remain fully selectable.
const THEMES = ['dark', 'light', 'navy', 'brownie', 'ai-fantasy', 'slate-finance', 'slate-finance-dark', 'console-dark', 'console-light'];

/**
 * ThemeProvider - Manages theme state and persistence
 *
 * Supports 'dark', 'light', 'navy', 'brownie', 'ai-fantasy', 'slate-finance',
 * 'slate-finance-dark', 'console-dark', and 'console-light' themes with
 * localStorage persistence. Applies theme by setting data-theme attribute on
 * document root.
 *
 * Default theme (issue #238): 'console-dark'. A user's saved preference
 * (localStorage 'portfolio-theme') always takes precedence over this default —
 * see the lazy useState initializer below, which only falls back to
 * `defaultTheme` when no valid saved value exists (new browser/logged-out
 * session, or a cleared/invalid localStorage entry). Existing users who
 * already have any theme saved (including legacy 'dark') are unaffected by
 * this default flip.
 */
export const ThemeProvider = ({ children, defaultTheme = 'console-dark' }) => {
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage or use default
    const savedTheme = localStorage.getItem('portfolio-theme');
    // Validate saved theme is one of the available themes
    return THEMES.includes(savedTheme) ? savedTheme : defaultTheme;
  });

  useEffect(() => {
    // Apply theme to document root
    document.documentElement.setAttribute('data-theme', theme);

    // Persist theme to localStorage
    localStorage.setItem('portfolio-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    // Cycle through all available themes
    setTheme(prevTheme => {
      const currentIndex = THEMES.indexOf(prevTheme);
      const nextIndex = (currentIndex + 1) % THEMES.length;
      return THEMES[nextIndex];
    });
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    availableThemes: THEMES,
    isDark: theme === 'dark',
    isLight: theme === 'light',
    isNavy: theme === 'navy',
    isBrownie: theme === 'brownie',
    isAiFantasy: theme === 'ai-fantasy',
    isSlateFinance: theme === 'slate-finance',
    isSlateFinanceDark: theme === 'slate-finance-dark',
    isConsoleDark: theme === 'console-dark',
    isConsoleLight: theme === 'console-light'
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme Hook - Access theme context
 *
 * @returns {Object} Theme context with { theme, setTheme, toggleTheme, isDark, isLight }
 * @throws {Error} If used outside ThemeProvider
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
